import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { Project, Service } from "@/lib/types";
import { Head, Screens } from "@/ui/frame/Page";
import { Trail } from "@/ui/Trail";
import { ErrorState, Skeleton } from "@/ui/States";
import { Button } from "@/ui/Button";
import { Lamp, serviceLabel, serviceLamp } from "@/ui/Lamp";
import { Chip, Handle } from "@/ui/Chip";
import { Notice, Settle } from "@/ui/Notice";
import { Strip, Reading } from "@/ui/Strip";
import { useToast } from "@/ui/Toast";
import { useService, serviceBase } from "@/features/services/useService";
import { describeResources } from "@/features/services/resources";
import { absoluteTime, relativeTime } from "@/lib/format";

export const Route = createFileRoute(
  "/_app/o/$orgId/projects/$projectId/services/$serviceId",
)({
  component: ServiceLayout,
});

const TAB_ROOT = "/o/$orgId/projects/$projectId/services/$serviceId";

interface Tab {
  label: string;
  to: string;
  visible?: boolean;
}

/**
 * The frame around one service: what it is, whether it is running, and a way
 * between the things you can look at.
 *
 * The header stays put while tabs change, so the state pill never disappears
 * from under someone who is watching a deploy.
 */
function ServiceLayout() {
  const { orgId, projectId, serviceId } = Route.useParams();
  const queryClient = useQueryClient();

  // For the trail. The service does not carry its project's name, and a
  // breadcrumb reading "projects / 01M4E… / api" is a trail nobody can read.
  // The same query the project screen uses, so arriving from there costs
  // nothing.
  const project = useQuery({
    queryKey: ["project", projectId],
    queryFn: ({ signal }) =>
      api.get<{ project: Project }>(
        `/organisations/${orgId}/projects/${projectId}`,
        undefined,
        signal,
      ),
  });
  const toast = useToast();

  const service = useService(orgId, projectId, serviceId);
  const base = serviceBase(orgId, projectId, serviceId);

  const setRunning = useMutation({
    mutationFn: (running: boolean) =>
      api.post<{ service: Service }>(`${base}/${running ? "start" : "stop"}`),
    onSuccess: async (_result, running) => {
      toast.success(running ? "Starting" : "Stopped");
      await queryClient.invalidateQueries({ queryKey: ["service", serviceId] });
    },
    onError: toast.error,
  });

  if (service.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }
  if (service.isError) {
    return <ErrorState error={service.error} onRetry={() => void service.refetch()} />;
  }

  const svc = service.data!.service;
  const running = svc.desired_state === "RUNNING";

  const tabs: Tab[] = [
    { label: "Overview", to: TAB_ROOT },
    { label: "Deployments", to: "/o/$orgId/projects/$projectId/services/$serviceId/deployments" },
    { label: "Logs", to: "/o/$orgId/projects/$projectId/services/$serviceId/logs" },
    { label: "Console", to: "/o/$orgId/projects/$projectId/services/$serviceId/console" },
    {
      label: "Domains",
      to: "/o/$orgId/projects/$projectId/services/$serviceId/domains",
      visible: svc.kind === "web",
    },
    { label: "Settings", to: "/o/$orgId/projects/$projectId/services/$serviceId/settings" },
  ];

  return (
    <>
      <Head
        above={
          <Trail
            trail={[
              { label: "Projects", to: "/o/$orgId/projects", params: { orgId } },
              {
                label: project.data?.project.display_name ?? "Project",
                to: "/o/$orgId/projects/$projectId",
                params: { orgId, projectId },
              },
              { label: svc.display_name },
            ]}
          />
        }
        /* The lamp leads. On a service the first question is never the name —
           it is whether the thing is alive — and the word for the state is in
           the strip below, where it sits beside the figures it belongs with
           and where it does not scroll away. */
        lamp={<Lamp state={serviceLamp(svc.state)} />}
        title={svc.display_name}
        actions={
          <Button
            tone={running ? "secondary" : "primary"}
            busy={setRunning.isPending}
            onClick={() => setRunning.mutate(!running)}
          >
            {running ? "Stop" : "Start"}
          </Button>
        }
        handles={
          <>
            <Chip>{svc.kind === "web" ? "Web service" : "Worker"}</Chip>
            <Chip>{describeResources(svc.resources)}</Chip>
            {svc.image ? (
              <Handle value={svc.image} label="Image" truncate={28} />
            ) : svc.git ? (
              <Chip>{`${svc.git.repository}@${svc.git.branch}`}</Chip>
            ) : null}
          </>
        }
        screens={
          <Screens
            items={tabs
              .filter((tab) => tab.visible !== false)
              .map((tab) => ({
                label: tab.label,
                to: tab.to,
                params: { orgId, projectId, serviceId },
                // The overview shares its path with the layout, so without
                // exact matching it stays lit while a child screen is open —
                // two tabs selected at once.
                exact: tab.to === TAB_ROOT,
              }))}
          />
        }
      />

      {/* What is true now, across the top, sticky. This is what the 320px
          spine was for, in 64 pixels of height instead of a fifth of the
          window — and the control that stops the service is in it, so it is
          still reachable from the bottom of a long log. */}
      <Strip>
        <Reading label="State" lamp={<Lamp state={serviceLamp(svc.state)} />}>
          {serviceLabel(svc.state)}
        </Reading>

        {svc.url && (
          <Reading label="Address" wide>
            <a
              href={svc.url}
              target="_blank"
              rel="noreferrer"
              className="truncate text-[var(--primary)] hover:underline"
            >
              {svc.url.replace(/^https?:\/\//, "")}
            </a>
          </Reading>
        )}

        <Reading label="Changed">
          <span title={absoluteTime(svc.state_changed_at)}>
            {relativeTime(svc.state_changed_at)}
          </span>
        </Reading>
      </Strip>

      {svc.state_reason && (
        <Notice
          tone={svc.state === "FAILED" ? "down" : "warn"}
          title={serviceLabel(svc.state)}
          className="mb-6"
        >
          {svc.state_reason}
        </Notice>
      )}

      <Settle>
        <Outlet />
      </Settle>
    </>
  );
}
